import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import MaskedTextInput from 'react-text-mask';
import { CalendarIcon, CloseCircleIcon } from '@components/icons';
import { DatePickerHeader } from '@components/moleculs/date-pickers/date-picker-header';
import { mask } from '@components/moleculs/date-pickers/date-picker/constants';
import type { DatePickerProps } from '@components/moleculs/date-pickers/date-picker/types';
import { DateFnsFormatEnum } from '@models/enums';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAppTheme } from '@theme';
import cn from 'classnames';
import { ar, de, es, fr, pt, ru } from 'date-fns/locale';

import { DatePickerContainer, FormHelperText } from '../styles';

registerLocale('ru', ru);
registerLocale('pt', pt);
registerLocale('es', es);
registerLocale('de', de);
registerLocale('fr', fr);
registerLocale('ar', ar);

export const DatePicker = /* forwardRef<HTMLInputElement, DatePickerProps>( */ (
    props: DatePickerProps /* , ref */,
) => {
    // const currentRef = useRef(ref);
    const { i18n } = useTranslation();
    const {
        containerClassName,
        hint,
        helperText,
        helperTextStatic,
        error,
        inputSize = 'm',
        value,
        onChange,
        showIcon = true,
        isClearable = true,
        placeholder = DateFnsFormatEnum.YEAR_MONTH_DAY,
        startYearRange,
        endYearRange,
        inputProps,
        ...rest
    } = props;

    const [dataProps, otherProps] = useMemo(() => {
        const data: Record<string, unknown> = {};
        const other: Record<string, unknown> = {};

        Object.entries(rest).forEach(([restKey, restValue]) => {
            if (restKey.startsWith('data-')) {
                data[restKey] = restValue;
            } else {
                other[restKey] = restValue;
            }
        });

        return [data, other];
    }, [rest]);

    const containerRef = useRef<HTMLDivElement>(null);

    const [clearIconContainer, setClearIconContainer] = useState<null | Element>(null);
    const [isOpen, setIsOpen] = useState(false);
    const theme = useAppTheme();
    const inputRef = useRef<HTMLInputElement>();
    const matchesMobileSm = useMediaQuery(`(max-width:${theme.breakPoints.sm})`);

    useEffect(() => {
        if (value && !clearIconContainer && containerRef.current) {
            const cleanIcons = (containerRef.current as Element).getElementsByClassName(
                'react-datepicker__close-icon',
            );

            if (cleanIcons[0]) {
                setClearIconContainer(cleanIcons[0]);
            }

            return;
        }
        if (!value && clearIconContainer) {
            setClearIconContainer(null);
        }
    }, [value, clearIconContainer]);

    const handleClearClick = useCallback(
        (e: React.SyntheticEvent) => {
            if (onChange) {
                onChange(null, e);
            }
        },
        [onChange],
    );

    const handleChangeCalendarDate = useCallback(
        (val: Date | null, event?: React.SyntheticEvent) => {
            if (onChange) {
                onChange(val, event as React.SyntheticEvent);
            }
        },
        [onChange],
    );

    const filterDate = useCallback(
        (date: Date) => (rest.disabledDate ? !rest.disabledDate(date) : false),
        [rest],
    );

    useEffect(() => {
        const eventLister = () => {
            if (document.activeElement !== inputRef.current) {
                inputRef.current?.focus();
            }
        };

        const element = containerRef.current;

        if (element) {
            element.addEventListener('click', eventLister);
        }

        return () => {
            if (element) {
                element.removeEventListener('click', eventLister);
            }
        };
    }, [containerRef]);

    return (
        <DatePickerContainer
            $inputSize={inputSize}
            error={error}
            className={cn(containerClassName, {
                'datepicker-open': isOpen,
                'datepicker-no-icon': !showIcon,
            })}
            ref={containerRef}
            {...dataProps}
            // onClick={(event) => {
            //     console.log(event, event.currentTarget);
            //     if (
            //         event.target instanceof HTMLElement &&
            //         event.target.classList.contains('react-datepicker__input-container')
            //     ) {
            //         inputRef.current?.focus();
            //     }
            // }}
        >
            <ReactDatePicker
                locale={i18n.language}
                {...otherProps}
                portalId={rest.portalId ?? 'root-date-picker'}
                renderCustomHeader={(params) => (
                    <DatePickerHeader
                        {...params}
                        startYearRange={startYearRange}
                        endYearRange={endYearRange}
                    />
                )}
                popperClassName={
                    rest.disableOpenOnMobile && matchesMobileSm ? 'mobile-hidden' : undefined
                }
                placeholderText={rest.label && !isOpen ? rest.label : placeholder}
                dateFormat={DateFnsFormatEnum.YEAR_MONTH_DAY}
                filterDate={rest.disabledDate ? filterDate : undefined}
                showIcon={showIcon}
                icon={rest.icon ?? <CalendarIcon className='calendar-icon' />}
                selectsMultiple={false}
                selected={value}
                onChange={handleChangeCalendarDate}
                isClearable={isClearable}
                showYearDropdown={rest.showYearDropdown ?? true}
                showMonthDropdown={rest.showMonthDropdown ?? true}
                onCalendarClose={() => {
                    setIsOpen(false);
                    rest.onCalendarClose?.();
                }}
                onCalendarOpen={() => {
                    setIsOpen(true);
                    rest.onCalendarOpen?.();
                }}
                popperPlacement={rest.popperPlacement ?? 'bottom-start'}
                toggleCalendarOnIconClick={rest.toggleCalendarOnIconClick ?? true}
                customInput={
                    rest.customInput ?? (
                        <MaskedTextInput
                            type='text'
                            className='datepicker-input'
                            mask={mask}
                            render={(textMaskRef, maskedInputProps) => (
                                <input
                                    {...maskedInputProps}
                                    {...inputProps}
                                    ref={(node) => {
                                        if (node) {
                                            textMaskRef(node as HTMLInputElement);
                                            inputRef.current = node;
                                        }
                                    }}
                                />
                            )}
                        />
                    )
                }
            />
            {!!clearIconContainer &&
                createPortal(
                    <CloseCircleIcon onClick={handleClearClick} className='clear-icon' />,
                    clearIconContainer,
                )}
            {(!!hint || !!helperText) && (
                <FormHelperText $helperTextStatic={helperTextStatic}>
                    <span className='helper-container'>
                        {!!hint && <span className='hint'>{hint}</span>}
                        {helperText}
                    </span>
                </FormHelperText>
            )}
        </DatePickerContainer>
    );
};
